import React, { useState } from 'react';
import { BackButton, TextButton as Button, InputLabel, RadioButton, Size, TextField } from '@laerdal/life-react-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { GrayBox, StepContainer, StepDescription, StepTitle } from './StepComponents';
import BreadcrumbTopSection from '../../../../commonComponents/BreadcrumbTopSection';
import { selectMarket, buildMarketPath } from '../../../../store/slices/globalConfigSlice';

interface Step8Props {
  onNext: () => void;
  onPrevious: () => void;
  handleResolvedStep: () => void;
  setDeviceType: (type: string) => void;
  setDeviceModel: (model: string) => void;
  setOperatingSystem: (os: string) => void;
  setOperatingSystemVersion: (version: string) => void;
}

const Step8: React.FC<Step8Props> = ({ onNext, onPrevious, handleResolvedStep, setDeviceType, setDeviceModel, setOperatingSystem, setOperatingSystemVersion }) => {
  const { t } = useTranslation(['translation', 'qcprSupport']);
  const currentMarket = useSelector(selectMarket);
  const [localDeviceType, setLocalDeviceType] = useState<string>('');
  const [localDeviceModel, setLocalDeviceModel] = useState<string>('');
  const [localOperatingSystem, setLocalOperatingSystem] = useState<string>('');
  const [localOperatingSystemVersion, setLocalOperatingSystemVersion] = useState<string>('');

  return (
    <StepContainer>
      <BreadcrumbTopSection
        setPadding={false}
        breadcrumb={[
          { text: t('Home', { ns: 'qcprSupport' }), link: buildMarketPath(currentMarket, '/') },
          { text: t('Software and Apps', { ns: 'qcprSupport' }), link: buildMarketPath(currentMarket, '/category/software-and-apps') },
          { text: t('QCPR App', { ns: 'qcprSupport' }), link: buildMarketPath(currentMarket, '/category/software-and-apps/product/qcpr-app') },
          { text: t('Troubleshooting', { ns: 'qcprSupport' }) }
        ]}
      />

      <BackButton onClick={onPrevious} size={Size.Small}>
        {t('Back', { ns: 'qcprSupport' })}
      </BackButton>

      <StepTitle>{t(`What mobile device are you experiencing issue with?`, { ns: 'qcprSupport' })}</StepTitle>
      <StepDescription>
        {t('Some device types may have different hardware capabilities.', { ns: 'qcprSupport' })}
        <strong>{t('We only support phone and tablet.', { ns: 'qcprSupport' })}</strong>
      </StepDescription>

      <GrayBox>
        <InputLabel required={true} inputId='deviceType' text={t('Choose the device', { ns: 'qcprSupport' })} />

        <RadioButton
          label={t('Phone', { ns: 'qcprSupport' })}
          selected={localDeviceType === 'phone'}
          select={(selected) => setLocalDeviceType(selected ? 'phone' : '')}
          onChange={() => { }}
        />

        <RadioButton
          label={t('Tablet', { ns: 'qcprSupport' })}
          selected={localDeviceType === 'tablet'}
          select={(selected) => setLocalDeviceType(selected ? 'tablet' : '')}
          onChange={() => { }}
        />

        <InputLabel inputId="deviceModel" text={t('Specify device model (if known)', { ns: 'qcprSupport' })} size={Size.Small} required={false} />
        <TextField
          id="deviceModel"
          data-testId="deviceModel"
          placeholder={t('e.g. Samsung Galaxy S25 Ultra', { ns: 'qcprSupport' })}
          size={Size.Medium}
          value={localDeviceModel}
          onChange={(value) => setLocalDeviceModel(value)}
        />


        <InputLabel required={true} inputId='deviceType' text={t('Choose the operating system', { ns: 'qcprSupport' })} />

        <RadioButton
          label={t('iOS', { ns: 'qcprSupport' })}
          selected={localOperatingSystem === 'iOS'}
          select={(selected) => setLocalOperatingSystem(selected ? 'iOS' : '')}
          onChange={() => { }}
        />

        <RadioButton
          label={t('Android', { ns: 'qcprSupport' })}
          selected={localOperatingSystem === 'Android'}
          select={(selected) => setLocalOperatingSystem(selected ? 'Android' : '')}
          onChange={() => { }}
        />

        <InputLabel inputId="operatingSystemVersion" text={t('Specify operating system version (if known)', { ns: 'qcprSupport' })} size={Size.Small} required={false} />
        <TextField
          id="operatingSystemVersion"
          data-testId="operatingSystemVersion"
          placeholder={t('e.g. iOS 18', { ns: 'qcprSupport' })}
          size={Size.Medium}
          value={localOperatingSystemVersion}
          onChange={(value) => setLocalOperatingSystemVersion(value)}
        />
      </GrayBox>


      <Button
        size={Size.Large}
        disabled={!localOperatingSystem || !localDeviceType}
        variant='primary'
        onClick={() => {
          // Pass the form data to parent component
          setDeviceType(localDeviceType);
          setDeviceModel(localDeviceModel);
          setOperatingSystem(localOperatingSystem);
          setOperatingSystemVersion(localOperatingSystemVersion);

          onNext();
        }}>
        {t("Next", { ns: 'qcprSupport' })}
      </Button>

      <StepDescription>{t('Your answers will help our team to assist you.', { ns: 'qcprSupport' })}</StepDescription>
    </StepContainer>
  );
};

export default Step8;
